export type SearchFilter = {
  fieldName: string;
  queryType?: string;
  values: string[];
};

export type FilterMap<T extends SearchFilter> = {
  [key: string]: T[];
};

export interface FilterSelector<T extends SearchFilter> {
  select(filters: FilterMap<T>, searchTerm: string): T[];
}

export interface FilterTransformer<T extends SearchFilter> {
  transform(filter: T, searchTerm: string): T;
}

export class FluentSearchFilterBuilder<T extends SearchFilter> {
  private searchTerm: string;

  private filterMap: FilterMap<T>;

  private selector: FilterSelector<T> = {
    select: (filters: FilterMap<T>) => {
      const keys = Object.keys(filters);
      return keys.length ? filters[keys[0]] : [];
    }
  };

  private transformer: FilterTransformer<T> = {
    transform: (filter: T, term: string) => ({
      ...filter,
      queryType: filter.queryType ?? 'wildcard',
      values: [`*${term}*`]
    })
  };

  constructor(filterMap: FilterMap<T> | T[], searchTerm?: string) {
    this.searchTerm = searchTerm ? searchTerm?.trim() : '';
    if (Array.isArray(filterMap)) {
      this.filterMap = filterMap.reduce((acc, filter) => {
        acc[filter.fieldName] = acc[filter.fieldName] || [];
        acc[filter.fieldName].push(filter);
        return acc;
      }, {} as FilterMap<T>);
    } else {
      this.filterMap = filterMap;
    }
  }

  public withSelector(selector: FilterSelector<T>): FluentSearchFilterBuilder<T> {
    this.selector = selector;
    return this;
  }

  public withTransformer(transformer: FilterTransformer<T>): FluentSearchFilterBuilder<T> {
    this.transformer = transformer;
    return this;
  }

  public addFilter(filter: T, key?: string): FluentSearchFilterBuilder<T> {
    let filterKey = key;
    if (!filterKey) {
      filterKey = filter.fieldName;
    }
    if (!this.filterMap[filterKey]) {
      this.filterMap[filterKey] = [];
    }
    this.filterMap[filterKey].push(filter);

    return this;
  }

  public setSearchTerm(searchTerm: string): FluentSearchFilterBuilder<T> {
    this.searchTerm = searchTerm;
    return this;
  }

  public build(): T[] {
    const selectedFilters = this.selector.select(this.filterMap, this.searchTerm);
    return selectedFilters.map((filter) => this.transformer.transform(filter, this.searchTerm));
  }
}
